import {getOpenCopilotChatVersion} from '@github-ui/copilot-chat-helpers/constants'
import {
  publishOpenCopilotChat,
  subscribeAddCopilotChatReference,
  subscribeOpenCopilotChat,
} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CopilotIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useEffect, useRef} from 'react'

import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

export function OpenCopilotChatPanelButton({
  size = 'small',
  variant = 'invisible',
  className,
}: {
  size?: 'small' | 'medium'
  variant?: 'invisible' | 'default'
  className?: string
}) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.CopilotChat)
  const actions = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // The PR page owns the docked container while chat consumers own its content.
  // Always open the container so whichever consumer handles the event has a
  // target to render into.
  useEffect(() => {
    return subscribeOpenCopilotChat(event => {
      const eventTrigger = event.payload.id ? document.getElementById(event.payload.id) : null
      if (eventTrigger) returnFocusRef.current = eventTrigger
      if (!isDockedPanelOpen) {
        actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo: eventTrigger ?? togglePanelRef.current})
      }
    })
  }, [actions, isDockedPanelOpen, returnFocusRef])

  // Intercept add-copilot-chat-reference events that request panel opening
  // so the active chat consumer can surface the reference in the side panel.
  useEffect(() => {
    return subscribeAddCopilotChatReference(e => {
      const eventTrigger = e.id ? document.getElementById(e.id) : null
      if (eventTrigger) returnFocusRef.current = eventTrigger
      if (e.openPanel && !isDockedPanelOpen) {
        actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo: eventTrigger ?? togglePanelRef.current})
      }
    })
  }, [actions, isDockedPanelOpen, returnFocusRef])

  const buttonAriaLabel = isDockedPanelOpen ? 'Close Copilot panel' : 'Open Copilot panel'

  return (
    <div className={`d-flex flex-items-center${className ? ` ${className}` : ''}`}>
      <ErrorBoundary
        boundaryName="OpenCopilotChatPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The Copilot panel cannot currently be opened."
            icon={CopilotIcon}
            size={size}
            variant={variant}
          />
        }
      >
        <IconButton
          ref={togglePanelRef}
          aria-label={buttonAriaLabel}
          aria-expanded={isDockedPanelOpen}
          icon={CopilotIcon}
          size={size}
          variant={variant}
          onClick={() => {
            // Emit the chat version so dashboards can distinguish v1 (immersive) vs v2
            // (ChatAppEmbedded) opens from this button. `copilot_chat_app_embedded`
            // gates which UI claims the docked panel — see `ChatAppEmbedded` and
            // `DockedCopilotChatPortal`.
            sendPullRequestAnalyticsEvent('copilot_chat_side_panel.open', 'COPILOT_CHAT_SIDE_PANEL_BUTTON', {
              chat_version: getOpenCopilotChatVersion({
                chatAppEmbeddedEnabled: isFeatureEnabled('copilot_chat_app_embedded'),
              }),
            })
            if (isDockedPanelOpen) {
              actions.closePanel()
            } else {
              // Fire the standard open event so handleOpenPanelEvent sets up
              // chat state (chatIsOpen, thread selection) before the panel DOM
              // mounts. The subscribeOpenCopilotChat handler above will open
              // the panel. This avoids a flash of stale thread content.
              publishOpenCopilotChat({intent: CopilotChatIntents.conversation})
            }
          }}
        />
      </ErrorBoundary>
    </div>
  )
}
